import {
  type InboundInventoryClientFilterKey,
  type InboundInventoryColumn,
  type InboundInventoryColumnDefinition,
  type InboundInventoryDateFilterKey,
  type InboundInventoryFilterDefinition,
  type InboundInventoryNumericFilterKey,
  type InboundInventoryPrimitiveFilterKey,
} from './inboundInventoriesTypes';
import { type UseExportInboundInventoriesParams, type UseGetInboundInventoriesParams } from './queries';

export const INBOUND_INVENTORY_DEFAULT_COLUMNS: InboundInventoryColumn[] = [
  'mplReference',
  'customer',
  'packingList',
  'inboundTransport',
  'transportDocument',
  'status',
  'terminal',
  'quantity',
  'quantityGrossWeight',
  'startInvoicingDate',
  'externalReference',
  'firstInboundDate',
];

export const INBOUND_INVENTORIES_INFINITE_INITIAL_PAGE_SIZE = 50;
export const INBOUND_INVENTORIES_INFINITE_NEXT_PAGE_SIZE = 10;

export const INBOUND_INVENTORY_QUERY_KEYS = {
  all: ['inventory-inbound'] as const,
  lists: () => [...INBOUND_INVENTORY_QUERY_KEYS.all, 'getAll'] as const,
  list: (params: UseGetInboundInventoriesParams, key?: string) => [...INBOUND_INVENTORY_QUERY_KEYS.lists(), key, params] as const,
  listInfinite: (params: Omit<UseGetInboundInventoriesParams, 'pagination'>, key?: string) => [...INBOUND_INVENTORY_QUERY_KEYS.lists(), 'infinite', key, params] as const,
  export: (params: UseExportInboundInventoriesParams) => [...INBOUND_INVENTORY_QUERY_KEYS.lists(), 'export', params] as const,
  details: () => [...INBOUND_INVENTORY_QUERY_KEYS.all, 'getOne'] as const,
  detail: (id: number) => [...INBOUND_INVENTORY_QUERY_KEYS.details(), id] as const,
};

export const INBOUND_INVENTORY_PRIMITIVE_FILTER_KEYS: InboundInventoryPrimitiveFilterKey[] = ['search', 'customer', 'portOfLoading', 'terminal'];

export const INBOUND_INVENTORY_NUMERIC_FILTER_KEYS: InboundInventoryNumericFilterKey[] = ['inboundGrossWeight', 'outboundGrossWeight', 'quantityGrossWeight'];

export const INBOUND_INVENTORY_DATE_FILTER_KEYS: InboundInventoryDateFilterKey[] = ['firstInboundDate', 'firstOutboundDate', 'startInvoicingDate', 'lastOutboundDate'];

export const INBOUND_INVENTORY_FILTER_KEYS = [...INBOUND_INVENTORY_PRIMITIVE_FILTER_KEYS, ...INBOUND_INVENTORY_NUMERIC_FILTER_KEYS, ...INBOUND_INVENTORY_DATE_FILTER_KEYS];

// Keep this fields in sync with the jsx components, because this is used to filter out the advanced filters when we clear them or count the amount of filters which are applied.
export const INBOUND_INVENTORY_ADVANCED_FILTERS: InboundInventoryClientFilterKey[] = [
  'inboundGrossWeight',
  'outboundGrossWeight',
  'quantityGrossWeight',
  'firstOutboundDate',
  'firstInboundDate',
  'lastOutboundDate',
  'startInvoicingDate',
];

export const INBOUND_INVENTORY_FILTER_DEFINITIONS: InboundInventoryFilterDefinition[] = [
  {
    field: 'firstOutboundDate',
    titleKey: 'filter.firstOutboundDate.title',
    keywords: ['outbound', 'first', 'date', 'dispatch', 'departure'],
  },
  {
    field: 'firstInboundDate',
    titleKey: 'filter.firstInboundDate.title',
    keywords: ['inbound', 'first', 'date', 'received', 'arrival'],
  },
  {
    field: 'inboundGrossWeight',
    titleKey: 'filter.inboundGrossWeight.title',
    keywords: ['inbound', 'weight', 'gross', 'kg', 'mass'],
  },
  {
    field: 'lastOutboundDate',
    titleKey: 'filter.lastOutboundDate.title',
    keywords: ['outbound', 'last', 'date', 'shipment', 'delivery'],
  },
  {
    field: 'outboundGrossWeight',
    titleKey: 'filter.outboundGrossWeight.title',
    keywords: ['outbound', 'weight', 'gross', 'kg', 'mass'],
  },
  {
    field: 'quantityGrossWeight',
    titleKey: 'filter.quantityGrossWeight.title',
    keywords: ['quantity', 'weight', 'gross', 'kg', 'amount'],
  },
  {
    field: 'startInvoicingDate',
    titleKey: 'filter.startInvoicingDate.title',
    keywords: ['invoicing', 'date', 'start', 'billing', 'billed'],
  },
];

// Column definitions for search and discovery in column order picker
export const INBOUND_INVENTORY_COLUMN_DEFINITIONS: InboundInventoryColumnDefinition[] = [
  { field: 'mplReference', titleKey: 'field.mplReference', keywords: ['mpl', 'reference', 'ref'] },
  { field: 'externalReference', titleKey: 'field.externalReference', keywords: ['external', 'reference', 'ref'] },
  { field: 'packingList', titleKey: 'field.packingList', keywords: ['packing', 'list'] },
  { field: 'transportDocument', titleKey: 'field.transportDocument', keywords: ['transport', 'document'] },
  { field: 'inboundTransport', titleKey: 'field.inboundTransport', keywords: ['inbound', 'transport'] },
  { field: 'status', titleKey: 'field.status', keywords: ['status', 'state'] },
  { field: 'quantity', titleKey: 'field.quantity', keywords: ['quantity', 'qty'] },
  { field: 'in', titleKey: 'field.inboundInventoryIn', keywords: ['in', 'inbound', 'received'] },
  { field: 'out', titleKey: 'field.inboundInventoryOut', keywords: ['out', 'outbound', 'sent'] },
  { field: 'terminal', titleKey: 'field.terminal', keywords: ['terminal', 'warehouse'] },
  { field: 'quantityGrossWeight', titleKey: 'field.quantityGrossWeight', keywords: ['quantity', 'gross', 'weight'] },
  { field: 'commodity', titleKey: 'field.commodity', keywords: ['commodity', 'goods'] },
  { field: 'customer', titleKey: 'field.customer', keywords: ['customer', 'client'] },
  { field: 'firstInboundDate', titleKey: 'field.firstInboundDate', keywords: ['first', 'inbound', 'date'] },
  { field: 'inboundGrossWeight', titleKey: 'field.inboundGrossWeight', keywords: ['inbound', 'gross', 'weight'] },
  { field: 'firstOutboundDate', titleKey: 'field.firstOutboundDate', keywords: ['first', 'outbound', 'date'] },
  { field: 'lastOutboundDate', titleKey: 'field.lastOutboundDate', keywords: ['last', 'outbound', 'date'] },
  { field: 'outboundGrossWeight', titleKey: 'field.outboundGrossWeight', keywords: ['outbound', 'gross', 'weight'] },
  { field: 'portOfLoading', titleKey: 'field.portOfLoading', keywords: ['pol', 'port of loading'] },
  { field: 'startInvoicingDate', titleKey: 'field.startInvoicingDate', keywords: ['invoicing', 'date', 'start'] },
];
